import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

// "Os meus pedidos" na área do cliente (10/10/2026): substitui as marcações do
// fluxo antigo. Só aparecem os pedidos feitos com a sessão iniciada (a BD liga-os
// à conta); os outros acompanham-se pelo link recebido no WhatsApp.

type MeuPedido = {
  codigo: string;
  estado: string;
  created_at: string;
  categoria: string | null;
  urgencia: string;
  prestador: string | null;
  token: string;
};

const ESTADO: Record<string, { texto: string; cor: string }> = {
  novo: { texto: 'A procurar prestador', cor: 'bg-blue-100 text-blue-800' },
  em_distribuicao: { texto: 'A procurar prestador', cor: 'bg-blue-100 text-blue-800' },
  escalado: { texto: 'Com a nossa equipa', cor: 'bg-orange-100 text-orange-800' },
  aceite: { texto: 'Aceite', cor: 'bg-green-100 text-green-800' },
  realizado: { texto: 'Por confirmar', cor: 'bg-teal-100 text-teal-800' },
  confirmado: { texto: 'Concluído', cor: 'bg-gray-100 text-gray-700' },
  expirado: { texto: 'Sem resposta', cor: 'bg-gray-100 text-gray-700' },
  cancelado: { texto: 'Cancelado', cor: 'bg-gray-100 text-gray-700' },
};

export default function MeusPedidos() {
  const [pedidos, setPedidos] = useState<MeuPedido[] | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    supabase.rpc('meus_pedidos').then(({ data, error }) => {
      if (!ativo) return;
      if (error) { setErro(true); setPedidos([]); return; }
      setPedidos((data ?? []) as MeuPedido[]);
    });
    return () => { ativo = false; };
  }, []);

  return (
    <section aria-labelledby="meus-pedidos" className="p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="meus-pedidos" className="text-lg font-semibold text-gray-900">Os meus pedidos</h2>
        <Link to="/pedir?origem=area_cliente"
          className="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          Pedir um serviço
        </Link>
      </div>

      {pedidos === null && <p className="text-sm text-gray-500">A carregar…</p>}
      {erro && <p role="alert" className="text-sm text-red-700">Não foi possível carregar os seus pedidos. Tente novamente mais tarde.</p>}
      {pedidos !== null && !erro && pedidos.length === 0 && (
        <p className="text-sm text-gray-600">
          Ainda não tem pedidos nesta conta. Os pedidos feitos sem iniciar sessão acompanham-se pelo link que recebeu no WhatsApp.
        </p>
      )}

      {!!pedidos?.length && (
        <ul className="divide-y divide-gray-100">
          {pedidos.map((p) => {
            const e = ESTADO[p.estado] ?? { texto: p.estado, cor: 'bg-gray-100 text-gray-700' };
            return (
              <li key={p.codigo} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900">{p.categoria ?? 'Pedido'}</div>
                  <div className="text-xs text-gray-500">
                    <span className="font-mono">{p.codigo}</span> · {new Date(p.created_at).toLocaleDateString('pt-PT', { timeZone: 'Africa/Luanda' })}
                    {p.prestador ? ` · ${p.prestador}` : ''}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${e.cor}`}>{e.texto}</span>
                  <Link to={`/pedido/${p.codigo}?t=${p.token}`} className="text-sm font-medium text-primary hover:underline">
                    Ver
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
